// Shared dropdown-menu kit — Walter 2026-06-26. Extracted from BoardPage's
// board ⋯ menu so the header, the left-nav board rows, and (later) the list
// menus all draw from ONE set of components and can't drift apart.
//
// - MenuItem: a row with an optional leading `icon` and ellipsised label.
// - SubMenu: a row that reveals a fly-out to its right on hover (desktop) /
//   tap (touch). Used for the colour + sort fly-outs.
// - ColorDot / ColorSwatches: the per-item colour swatch + picker grid.
// - Icon*: small line-art glyphs that inherit currentColor.
import React, { useState, useRef, useLayoutEffect } from 'react';
import { LIST_COLORS, getListColorValue } from '../lib/listColors';

export function MenuItem({ children, onClick, color, icon, title, indent }) {
  return (
    <div
      onClick={onClick}
      title={title}
      style={{
        // indent → nest under a preceding section label (e.g. the board
        // menu's "sort lists" header). 37px aligns the text under a labelled
        // row's text (14 base + 14 icon + 9 gap).
        padding: indent ? '8px 14px 8px 37px' : '8px 14px', cursor: 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: color || 'var(--text-dim)',
        transition: 'background 0.1s, color 0.1s',
        display: 'flex', alignItems: 'center', gap: '9px',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; if (!color) e.currentTarget.style.color = 'var(--text)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; if (!color) e.currentTarget.style.color = 'var(--text-dim)'; }}
    >
      {icon !== undefined && (
        <span style={{ display: 'inline-flex', width: 14, justifyContent: 'center', flexShrink: 0, opacity: 0.85 }}>{icon}</span>
      )}
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
    </div>
  );
}

// Small line-art icons for menu rows. 14px, inherit currentColor.
const ICON_SVG = { width: 14, height: 14, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round', strokeLinejoin: 'round' };
export function IconPencil() { return <svg {...ICON_SVG}><path d="M11.3 2.4l2.3 2.3-7.7 7.7-2.9.6.6-2.9z" /></svg>; }
export function IconColor() { return <svg {...ICON_SVG}><circle cx="8" cy="8" r="5.5" /><circle cx="8" cy="8" r="1.7" fill="currentColor" stroke="none" /></svg>; }
export function IconShare() { return <svg {...ICON_SVG}><circle cx="4" cy="8" r="1.7" /><circle cx="12" cy="3.6" r="1.7" /><circle cx="12" cy="12.4" r="1.7" /><path d="M5.5 7.1l5-2.7M5.5 8.9l5 2.7" /></svg>; }
export function IconSort() { return <svg {...ICON_SVG}><path d="M4 4h8M4 8h5.5M4 12h3" /></svg>; }
export function IconTrash() { return <svg {...ICON_SVG}><path d="M3 4.5h10M6.2 4.5V3h3.6v1.5M4.6 4.5l.6 8.5h5.6l.6-8.5" /></svg>; }
export function IconPlus() { return <svg {...ICON_SVG}><path d="M8 3.5v9M3.5 8h9" /></svg>; }

// A filled colour dot for the current board/list swatch. Renders nothing when
// no colour is set.
export function ColorDot({ colorId, size = 8 }) {
  const v = getListColorValue(colorId);
  if (!v) return null;
  return <span style={{ width: size, height: size, borderRadius: '50%', background: v, flexShrink: 0, display: 'inline-block' }} />;
}

// Swatch grid shown inside the "colour" fly-out. One click applies; the first
// swatch is a dashed "none" that clears the colour.
export function ColorSwatches({ current, onPick }) {
  const dot = (id, value, label) => {
    const selected = (current || null) === (id || null);
    return (
      <button
        key={id || 'none'} type="button" title={label}
        onClick={() => onPick(id)}
        style={{
          width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: value || 'transparent',
          border: value
            ? (selected ? '2px solid var(--text)' : '1px solid rgba(0,0,0,0.25)')
            : '1px dashed var(--border-bright)',
        }}
      >{!value ? <span style={{ fontSize: 10, color: 'var(--text-dim)', lineHeight: 1 }}>×</span> : null}</button>
    );
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', padding: '9px 10px', width: '132px' }}>
      {dot(null, null, 'none')}
      {LIST_COLORS.map(c => dot(c.id, c.value, c.label))}
    </div>
  );
}

// A menu row that reveals a fly-out panel to its side — on hover (desktop) and
// on tap (touch, which has no hover). `align` is the PREFERRED side ('right' by
// default); when there isn't room on that side (narrow viewport / mobile
// drawer), it auto-flips to the other side so the fly-out never lands off-
// screen. Walter 2026-06-26; auto-flip 2026-07-02.
const SUBMENU_FLYOUT_W = 170; // minWidth 150 + border/padding — overflow estimate

export function SubMenu({ icon, label, children, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState(align);
  const rowRef = useRef(null);
  // On touch (no hover), a tap fires a SYNTHETIC mouseenter AND a click — the
  // mouseenter opened the fly-out and the click immediately toggled it shut, so
  // it never stayed open (Walter 2026-07-02: "fly-outs don't work on mobile").
  // Only wire the hover handlers on hover-capable devices; touch uses the click
  // toggle alone.
  const [canHover] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: hover)').matches);

  useLayoutEffect(() => {
    if (!open || !rowRef.current || typeof window === 'undefined') return;
    const r = rowRef.current.getBoundingClientRect();
    const roomRight = window.innerWidth - r.right;
    const roomLeft = r.left;
    if (align === 'right') {
      setSide(roomRight < SUBMENU_FLYOUT_W && roomLeft > roomRight ? 'left' : 'right');
    } else {
      setSide(roomLeft < SUBMENU_FLYOUT_W && roomRight > roomLeft ? 'right' : 'left');
    }
  }, [open, align]);

  const flyoutPos = side === 'left'
    ? { right: '100%', marginRight: '2px' }
    : { left: '100%', marginLeft: '2px' };
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={canHover ? () => setOpen(true) : undefined}
      onMouseLeave={canHover ? () => setOpen(false) : undefined}
    >
      <div
        ref={rowRef}
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '8px 14px', cursor: 'pointer', fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: open ? 'var(--text)' : 'var(--text-dim)',
          background: open ? 'var(--surface2)' : 'transparent',
          display: 'flex', alignItems: 'center', gap: '9px',
        }}
      >
        {icon !== undefined && (
          <span style={{ display: 'inline-flex', width: 14, justifyContent: 'center', flexShrink: 0, opacity: 0.85 }}>{icon}</span>
        )}
        {side === 'left' && <span style={{ color: 'var(--text-dimmer)' }}>‹</span>}
        <span style={{ flex: 1 }}>{label}</span>
        {side !== 'left' && <span style={{ color: 'var(--text-dimmer)' }}>›</span>}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 0, ...flyoutPos,
          background: 'var(--surface)', border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)', minWidth: '150px',
          boxShadow: 'var(--shadow-popover)', zIndex: 1001, overflow: 'hidden', padding: '2px 0',
        }}>
          {children}
        </div>
      )}
    </div>
  );
}
